import React, { useRef } from 'react';
import { Card, Row, Col, Typography, Button } from 'antd';
import { Pie } from '@ant-design/plots';
import { DownloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import * as htmlToImage from 'html-to-image';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import download from 'downloadjs';

import { formatIndicatorValueForChart } from '../../../../core/utils/helpers';
import { SchoolComparison, SpecialIndicator, IndicatorSummary } from '../../../../core/indicator/types';
import { CombinedStates } from '../../../../core/types';
import BarChart from './BarChart';

const { Title, Text } = Typography;

interface SchoolComparisonResultsProps {
    schoolComparison: SchoolComparison;
    specialIndicators?: SpecialIndicator[];
    forIndividualSchool?: boolean;
}

const defaultProps = {
    specialIndicators: [],
    forIndividualSchool: false,
};

const SchoolComparisonResults: React.FC<SchoolComparisonResultsProps> = ({ schoolComparison, specialIndicators, forIndividualSchool = false }) => {
    // Get server date and indicators summary from Redux
    const { serverCompareDate, indicatorsSummary } = useSelector((state: CombinedStates) => state.indicator);

    // Create refs for each indicator's containers
    const containerRefs = useRef<{ [key: string]: { container1: HTMLDivElement | null; container2: HTMLDivElement | null } }>({});

    // Helper function to get indicator summary by ID
    const getIndicatorSummary = (indicatorId: number): IndicatorSummary | null => indicatorsSummary.find(summary => summary.id === indicatorId) || null;

    // Helper function to calculate max value for comparison
    const getMaxValueForComparison = (indicatorData: any[]) => {
        const values = indicatorData
            .filter(indicator => indicator.has_data)
            .map(indicator => formatIndicatorValueForChart(indicator.value, indicator.indicator_unit, indicator.has_data).numericValue);

        if (values.length === 0) return 100;

        const maxValue = Math.max(...values);
        // Round up to nearest 10, 100, 1000 etc. for better visualization
        const magnitude = 10 ** Math.floor(Math.log10(maxValue));
        return Math.ceil(maxValue / magnitude) * magnitude;
    };

    // Download functions
    const downloadContainer1 = async (indicatorId: string) => {
        const container = containerRefs.current[indicatorId]?.container1;
        if (container) {
            try {
                const dataUrl = await htmlToImage.toPng(container, {
                    quality: 1,
                    pixelRatio: 2,
                    backgroundColor: '#ffffff',
                });
                download(dataUrl, `rodikliu-palyginimas-R${indicatorId}-${new Date().toISOString().split('T')[0]}.png`);
            } catch (error) {
                console.error('Error downloading container 1:', error);
            }
        }
    };

    const downloadContainer2 = async (indicatorId: string) => {
        const container = containerRefs.current[indicatorId]?.container2;
        if (container) {
            try {
                const dataUrl = await htmlToImage.toPng(container, {
                    quality: 1,
                    pixelRatio: 2,
                    backgroundColor: '#ffffff',
                });
                download(dataUrl, `reiksmiu-pasiskirstymas-R${indicatorId}-${new Date().toISOString().split('T')[0]}.png`);
            } catch (error) {
                console.error('Error downloading container 2:', error);
            }
        }
    };

    return (
        <Card title={`Palyginimo rezultatai (${serverCompareDate ? serverCompareDate.replace(',', ' - ') : 'N/A'})`} className="results-card">
            <div className="comparison-results">
                {/* Summary */}
                <div className="comparison-summary">
                    <Row gutter={[16, 16]}>
                        {!forIndividualSchool && (
                            <Col xs={24} md={6}>
                                <Card size="small" title="Bendri duomenys">
                                    <div className="summary-item">
                                        <Text strong>Iš viso rodiklių:</Text>
                                        <Text>{schoolComparison.summary.total_indicators}</Text>
                                    </div>
                                    <div className="summary-item">
                                        <Text strong>Iš viso mokyklų:</Text>
                                        <Text>{schoolComparison.summary.total_schools}</Text>
                                    </div>
                                    <div className="summary-item">
                                        <Text strong>Visose mokyklose:</Text>
                                        <Text>{schoolComparison.summary.indicators_with_data_all_schools}</Text>
                                    </div>
                                </Card>
                            </Col>
                        )}
                        {schoolComparison.summary.schools_data_completion_rates?.map((schoolData) => (
                            <Col xs={24} md={6} key={schoolData.school_id}>
                                <Card size="small" title={schoolData.school_name}>
                                    <div className="summary-item">
                                        <Text strong>Duomenų užpildymas:</Text>
                                        <Text>{schoolData.completion_rate}%</Text>
                                    </div>
                                    <div className="summary-item">
                                        <Text strong>Rodikliai su duomenimis:</Text>
                                        <Text>{schoolData.indicators_with_data}</Text>
                                    </div>
                                    {forIndividualSchool && (
                                        <div className="summary-item">
                                            <Text strong>Iš viso rodiklių:</Text>
                                            <Text>{schoolComparison.summary.total_indicators}</Text>
                                        </div>
                                    )}
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>

                {/* Detailed Comparison */}
                <div className="comparison-details">
                    <Title level={5}>Detalus palyginimas</Title>
                    <div className="indicators-comparison">
                        {(() => {
                            // Get all unique indicators from all schools
                            const allIndicators = new Map();
                            schoolComparison.schools?.forEach(school => {
                                school.indicators?.forEach(indicator => {
                                    if (!allIndicators.has(indicator.indicator_id)) {
                                        allIndicators.set(indicator.indicator_id, {
                                            id: indicator.indicator_id,
                                            title: indicator.indicator_title,
                                            unit: indicator.indicator_unit,
                                        });
                                    }
                                });
                            });

                            return Array.from(allIndicators.values()).map((indicator) => {
                                // Get indicator data from all schools
                                const schoolIndicatorData = schoolComparison.schools?.map(school => {
                                    const indicatorData = school.indicators?.find(i => i.indicator_id === indicator.id);
                                    return {
                                        schoolId: school.id,
                                        schoolName: school.name,
                                        indicator: indicatorData,
                                    };
                                });

                                // Add special indicators data to each indicator
                                specialIndicators?.forEach(specialIndicator => {
                                    schoolIndicatorData.push({
                                        schoolId: -1,
                                        schoolName: `${specialIndicator.title} (${specialIndicator.assigned_scope === 'education_center' ? 'Švietimo centro' : 'Švietimo skyriaus'} rodiklis)`,
                                        indicator: {
                                            indicator_id: specialIndicator.id,
                                            indicator_title: specialIndicator.title,
                                            indicator_unit: '',
                                            value: parseFloat(specialIndicator.value) || 0,
                                            value_text: null,
                                            period: specialIndicator.period,
                                            period_description: specialIndicator.period_description,
                                            has_data: true,
                                            last_updated: new Date().toISOString(),
                                        },
                                    });
                                });

                                // Calculate max value for this indicator comparison
                                const normalizedMax = getMaxValueForComparison(
                                    schoolIndicatorData?.map(s => s.indicator).filter(Boolean) || [],
                                );

                                // Check if we should show bar chart (skip for taip_ne and text units)
                                const shouldShowBarChart = indicator.unit !== 'taip_ne' && indicator.unit !== 'text';

                                // Generate colors for schools
                                const colors = ['#1890ff', '#52c41a', '#fa8c16', '#eb2f96', '#13c2c2', '#722ed1', '#fadb14', '#2f54eb'];

                                return (
                                    <Card key={indicator.id} size="small" className="indicator-comparison-card">
                                        <Row gutter={16}>
                                            <Col xs={24} md={6}>
                                                <div className="indicator-info">
                                                    <Text strong>R{indicator.id} - {indicator.title}</Text>
                                                    <Text type="secondary">{indicator.unit}</Text>
                                                </div>
                                            </Col>
                                            <Col xs={24} md={18}>
                                                {/* Indicator Summary */}
                                                {(() => {
                                                    const summary = getIndicatorSummary(indicator.id);
                                                    if (!summary) return null;

                                                    return (
                                                        <div style={{
                                                            backgroundColor: '#f6ffed',
                                                            border: '1px solid #b7eb8f',
                                                            borderRadius: '6px',
                                                            padding: '12px',
                                                            marginBottom: '16px',
                                                        }}>
                                                            <Text strong style={{ color: '#52c41a', display: 'block', marginBottom: '8px' }}>
                                                                📊 Rodiklio statistika ({summary.period_description})
                                                            </Text>
                                                            <Row gutter={[16, 8]}>
                                                                <Col xs={12} sm={6}>
                                                                    <Text type="secondary" style={{ fontSize: '12px' }}>Mokyklos su duomenimis:</Text>
                                                                    <Text strong style={{ display: 'block' }}>{summary.statistics.schools_with_data} / {summary.statistics.total_schools}</Text>
                                                                </Col>
                                                                <Col xs={12} sm={6}>
                                                                    <Text type="secondary" style={{ fontSize: '12px' }}>Vidurkis:</Text>
                                                                    <Text strong style={{ display: 'block' }}>
                                                                        {summary.statistics.average != null ? summary.statistics.average.toFixed(2) : 'N/A'} {summary.unit}
                                                                    </Text>
                                                                </Col>
                                                                <Col xs={12} sm={6}>
                                                                    <Text type="secondary" style={{ fontSize: '12px' }}>Min:</Text>
                                                                    <Text strong style={{ display: 'block' }}>{summary.statistics.min || 'N/A'} {summary.unit}</Text>
                                                                </Col>
                                                                <Col xs={12} sm={6}>
                                                                    <Text type="secondary" style={{ fontSize: '12px' }}>Max:</Text>
                                                                    <Text strong style={{ display: 'block' }}>{summary.statistics.max || 'N/A'} {summary.unit}</Text>
                                                                </Col>
                                                                <Col xs={12} sm={6}>
                                                                    <Text type="secondary" style={{ fontSize: '12px' }}>Mediana:</Text>
                                                                    <Text strong style={{ display: 'block' }}>
                                                                        {summary.statistics.median != null ? summary.statistics.median : 'N/A'} {summary.unit}
                                                                    </Text>
                                                                </Col>
                                                                <Col xs={12} sm={6}>
                                                                    <Text type="secondary" style={{ fontSize: '12px' }}>Suma:</Text>
                                                                    <Text strong style={{ display: 'block' }}>
                                                                        {summary.statistics.sum != null ? summary.statistics.sum : 'N/A'} {summary.unit}
                                                                    </Text>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    );
                                                })()}

                                                {shouldShowBarChart ? (
                                                    <div className="comparison-chart">
                                                        <div className="chart-container-with-download">
                                                            <div className="chart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                                <Text strong>Rodiklių palyginimas</Text>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<DownloadOutlined />}
                                                                    onClick={() => downloadContainer1(indicator.id)}>
                                                                    Atsisiųsti
                                                                </Button>
                                                            </div>
                                                            <div
                                                                className="able-to-download-container-1"
                                                                ref={(el) => {
                                                                    if (!containerRefs.current[indicator.id]) {
                                                                        containerRefs.current[indicator.id] = { container1: null, container2: null };
                                                                    }
                                                                    const refs = containerRefs.current[indicator.id];
                                                                    if (refs) {
                                                                        refs.container1 = el;
                                                                    }
                                                                }}>
                                                                <BarChart
                                                                    data={schoolIndicatorData?.map((schoolData, index) => {
                                                                        const isSpecialIndicator = schoolData.schoolId === -1;
                                                                        return {
                                                                            label: schoolData.schoolName,
                                                                            value: formatIndicatorValueForChart(
                                                                                schoolData.indicator?.value || 0,
                                                                                indicator.unit,
                                                                                schoolData.indicator?.has_data || false,
                                                                            ).numericValue,
                                                                            displayValue: formatIndicatorValueForChart(
                                                                                schoolData.indicator?.value || 0,
                                                                                indicator.unit,
                                                                                schoolData.indicator?.has_data || false,
                                                                            ).displayValue,
                                                                            color: isSpecialIndicator ? '#52c41a' : (colors[index % colors.length] || '#1890ff'),
                                                                            hasData: schoolData.indicator?.has_data || false,
                                                                            periodDescription: schoolData.indicator?.period_description,
                                                                            isSpecial: isSpecialIndicator,
                                                                        };
                                                                    })}
                                                                    maxValue={normalizedMax}
                                                                    height={100}
                                                                    showDifference={false} />
                                                            </div>
                                                        </div>
                                                        <div className="pie-chart-container-with-download">
                                                            {(() => {
                                                                const pieData = schoolIndicatorData
                                                                    ?.filter(schoolData => schoolData.indicator?.has_data)
                                                                    ?.map((schoolData) => ({
                                                                        type: schoolData.schoolName,
                                                                        Rezultatas: formatIndicatorValueForChart(
                                                                            schoolData.indicator?.value || 0,
                                                                            indicator.unit,
                                                                            schoolData.indicator?.has_data || false,
                                                                        ).numericValue,
                                                                    }));

                                                                if (!pieData || pieData.length <= 1) return null;

                                                                const config = {
                                                                    data: pieData,
                                                                    angleField: 'Rezultatas',
                                                                    colorField: 'type',
                                                                    radius: 0.8,
                                                                    label: {
                                                                        text: (d: { type: string; Rezultatas: number }) => `${d.type}: ${d.Rezultatas}`,
                                                                        position: 'outside',
                                                                        connector: true,
                                                                    },
                                                                    tooltip: {
                                                                        title: (d: { type: string }) => d.type,
                                                                    },
                                                                    legend: { position: 'bottom' },
                                                                    interactions: [{ type: 'element-active' }],
                                                                };

                                                                return (
                                                                    <div style={{ marginTop: '16px' }}>
                                                                        <div className="pie-chart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                                            <Text strong style={{ fontSize: '14px' }}>
                                                                                Reikšmių pasiskirstymas:
                                                                            </Text>
                                                                            <Button
                                                                                type="default"
                                                                                size="small"
                                                                                icon={<DownloadOutlined />}
                                                                                onClick={() => downloadContainer2(indicator.id)}>
                                                                                Atsisiųsti
                                                                            </Button>
                                                                        </div>
                                                                        <div
                                                                            className="able-to-download-container-2"
                                                                            ref={(el) => {
                                                                                if (!containerRefs.current[indicator.id]) {
                                                                                    containerRefs.current[indicator.id] = { container1: null, container2: null };
                                                                                }
                                                                                const refs = containerRefs.current[indicator.id];
                                                                                if (refs) {
                                                                                    refs.container2 = el;
                                                                                }
                                                                            }}
                                                                            style={{ minHeight: '200px', backgroundColor: '#f0f0f0', borderRadius: '8px', padding: '16px' }}>
                                                                            <Pie {...config} />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-comparison">
                                                        {schoolIndicatorData?.map((schoolData) => {
                                                            const isSpecialIndicator = schoolData.schoolId === -1;
                                                            return (
                                                                <div
                                                                    key={schoolData.schoolId}
                                                                    className={`school-value ${isSpecialIndicator ? 'special-indicator-value' : ''}`}
                                                                    style={isSpecialIndicator ? {
                                                                        borderLeft: '3px solid #52c41a',
                                                                        paddingLeft: '8px',
                                                                        backgroundColor: '#f6ffed',
                                                                        marginTop: '8px',
                                                                    } : {}}>
                                                                    <div>
                                                                        <Text strong style={{ color: isSpecialIndicator ? '#52c41a' : undefined }}>
                                                                            {schoolData.schoolName}:
                                                                        </Text>
                                                                        {(() => {
                                                                            const formatted = formatIndicatorValueForChart(
                                                                                schoolData.indicator?.value || 0,
                                                                                indicator.unit,
                                                                                schoolData.indicator?.has_data || false,
                                                                            );
                                                                            return formatted.color ? (
                                                                                <Text style={{ color: formatted.color, fontWeight: 'bold' }}>{formatted.displayValue}</Text>
                                                                            ) : (
                                                                                <Text>{formatted.displayValue}</Text>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                    {schoolData.indicator?.period_description && (
                                                                        <div style={{ marginTop: 4 }}>
                                                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                                                {schoolData.indicator.period_description}
                                                                            </Text>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    </Card>
                                );
                            });
                        })()}
                    </div>
                </div>

            </div>
        </Card>
    );
};

SchoolComparisonResults.defaultProps = defaultProps;

export default SchoolComparisonResults;
